import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Badge,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Progress,
    Table,
    Spinner,
    Button
} from 'reactstrap';
import { BsFillBarChartLineFill, BsFillPieChartFill, BsDownload, BsCalendar3 } from 'react-icons/bs';
import { FaChartLine, FaClipboardList, FaRegChartBar, FaBuilding } from 'react-icons/fa';
import { LuBuilding2 } from 'react-icons/lu';
import Chart from 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';
import { Bar } from 'react-chartjs-2';
import { themeColors } from '../../../style/color';

// Enhanced chart options
const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top',
            labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                    size: 12,
                    weight: '500'
                }
            }
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: themeColors.primary,
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
        }
    },
    scales: {
        x: {
            grid: {
                display: false
            },
            ticks: {
                font: {
                    size: 11,
                    weight: '500'
                }
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
                font: {
                    size: 11
                }
            }
        }
    },
    elements: {
        bar: {
            borderRadius: 6,
            borderSkipped: false,
        }
    }
};

const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'right',
            labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                    size: 12,
                    weight: '500'
                }
            }
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: themeColors.primary,
            borderWidth: 1,
            cornerRadius: 8,
        }
    },
    cutout: '60%',
    elements: {
        arc: {
            borderWidth: 2,
            borderColor: '#fff',
            hoverBorderWidth: 3
        }
    }
};

const GraphicalReport = () => {
    const [loading, setLoading] = useState(true);
    const [graphData, setGraphData] = useState([]);
    const [doughnutData, setDoughnutData] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        get_graph();
    }, []);

    const get_graph = async () => {
        setLoading(true);

        const postData = [{
            "org_id": sessionStorage.getItem('org_id'),
            "user_code": sessionStorage.getItem('user_id'),
            "password": sessionStorage.getItem('user_pass')
        }];

        try {
            const response = await axios({
                method: 'post',
                url: mainurl + 'Dashboard/db_distribution_2',
                data: postData,
                headers: { 'Content-Type': 'application/json' }
            });

            // Process the response data
            const labels = [];
            const b_data = [];

            response.data?.forEach(row => {
                labels.push(row.org_name);
                b_data.push(Number(row.total_farmer));
            });

            // Create doughnut chart data
            const dataPie = {
                maintainAspectRatio: true,
                responsive: true,
                labels: labels,
                datasets: [
                    {
                        data: b_data,
                        backgroundColor: [
                            themeColors.primary,
                            themeColors.secondary,
                            themeColors.info,
                            themeColors.danger
                        ],
                        borderWidth: 0,
                        hoverOffset: 8
                    }
                ]
            };

            setDoughnutData(dataPie);

            // Create bar chart data
            const temp = [
                {
                    label: "Beneficiaries",
                    data: b_data,
                    backgroundColor: `${themeColors.primary}cc`,
                    borderColor: themeColors.primary,
                    borderWidth: 2,
                    borderRadius: 8,
                    borderSkipped: false,
                }
            ];

            setGraphData({
                "labels": labels,
                "datasets": temp
            });

        } catch (error) {
            console.error('Error fetching graph data:', error);
            // Handle error state - you might want to show an error message to the user
            // setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const totalBeneficiaries = doughnutData?.datasets?.[0]?.data?.reduce((a, b) => a + b, 0) || 0;
    const avgBeneficiaries = Math.round(totalBeneficiaries / (doughnutData?.labels?.length || 1));

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    return (
        <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-bold">
                                <FaBuilding className="me-2" style={{ color: themeColors.success }} />
                                CSO Wise Anlaysis
                            </h5>
                            <p className="text-muted mb-0">Comprehensive overview of CSO performance</p>
                        </div>
                        <Badge color="success" pill className="px-3 py-2">
                            <BsCalendar3 size={14} className="me-1" />
                            Live Data
                        </Badge>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <Nav tabs className="border-0">
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'overview' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('overview')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <FaRegChartBar className="me-2" />
                                            Overview
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'details' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('details')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <FaClipboardList className="me-2" />
                                            Details
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'trends' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('trends')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <FaChartLine className="me-2" />
                                            Trends
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <div className="d-flex align-items-center gap-2">
                                    <Button color="outline-primary" size="sm">
                                        <BsDownload className="me-1" />
                                        Export
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                    {/* Charts */}
                    <Row>
                        <Col lg={6} className="mb-4">
                            <Card className="h-100 shadow-sm border-0">
                                <CardHeader className="bg-gradient" style={{ background: 'linear-gradient(135deg, #3b82f620 0%, #1e40af10 100%)' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <BsFillBarChartLineFill size={20} className="me-2" style={{ color: themeColors.primary }} />
                                            <div>
                                                <CardTitle className="mb-0 fw-semibold">Beneficiaries by Organization</CardTitle>
                                                <small className="text-muted">Distribution across CSOs</small>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <h4 className="mb-0 fw-bold" style={{ color: themeColors.primary }}>{totalBeneficiaries}</h4>
                                            <small className="text-muted">Total</small>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                            <Spinner color="primary" />
                                        </div>
                                    ) : (
                                        <div style={{ height: '300px' }}>
                                            <Bar options={barChartOptions} data={graphData} />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={6} className="mb-4">
                            <Card className="h-100 shadow-sm border-0">
                                <CardHeader className="bg-gradient" style={{ background: 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed10 100%)' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <BsFillPieChartFill size={20} className="me-2" style={{ color: themeColors.secondary }} />
                                            <div>
                                                <CardTitle className="mb-0 fw-semibold">Distribution Overview</CardTitle>
                                                <small className="text-muted">Proportional representation</small>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <h4 className="mb-0 fw-bold" style={{ color: themeColors.secondary }}>100%</h4>
                                            <small className="text-muted">Coverage</small>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                            <Spinner color="secondary" />
                                        </div>
                                    ) : (
                                        <div style={{ height: '300px' }}>
                                            <Doughnut options={doughnutChartOptions} data={doughnutData} />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane tabId="details">
                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0">
                                <CardHeader>
                                    <CardTitle className="mb-0 fw-semibold">Detailed Breakdown</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {doughnutData?.labels?.map((label, index) => {
                                            const value = doughnutData.datasets[0].data[index];
                                            const percentage = ((value / totalBeneficiaries) * 100).toFixed(1);
                                            return (
                                                <Col md={3} key={label} className="mb-3">
                                                    <Card className="border" style={{ borderColor: doughnutData.datasets[0].backgroundColor[index] + '40' }}>
                                                        <CardBody className="text-center">
                                                            <div
                                                                className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    backgroundColor: doughnutData.datasets[0].backgroundColor[index] + '20',
                                                                    color: doughnutData.datasets[0].backgroundColor[index]
                                                                }}
                                                            >
                                                                <LuBuilding2 size={20} />
                                                            </div>
                                                            <h6 className="fw-semibold mb-1">{label}</h6>
                                                            <h4 className="mb-1 fw-bold">{value?.toLocaleString()}</h4>
                                                            <Badge
                                                                color="light"
                                                                className="mb-2"
                                                                style={{ color: doughnutData.datasets[0].backgroundColor[index] }}
                                                            >
                                                                {percentage}%
                                                            </Badge>
                                                            <Progress
                                                                value={percentage}
                                                                color="primary"
                                                                style={{ height: '4px' }}
                                                            />
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>

                                    <hr className="my-4" />

                                    <Table responsive striped>
                                        <thead>
                                            <tr>
                                                <th>Organization</th>
                                                <th>Beneficiaries</th>
                                                <th>Percentage</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {doughnutData?.labels?.map((label, index) => {
                                                const value = doughnutData.datasets[0].data[index];
                                                const percentage = ((value / totalBeneficiaries) * 100).toFixed(1);
                                                return (
                                                    <tr key={label}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div
                                                                    className="rounded me-2"
                                                                    style={{
                                                                        width: '12px',
                                                                        height: '12px',
                                                                        backgroundColor: doughnutData.datasets[0].backgroundColor[index]
                                                                    }}
                                                                />
                                                                <strong>{label}</strong>
                                                            </div>
                                                        </td>
                                                        <td>{value?.toLocaleString()}</td>
                                                        <td>
                                                            <Badge color={percentage > 30 ? 'success' : percentage > 20 ? 'warning' : 'danger'}>
                                                                {percentage}%
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge color={value > 400 ? 'success' : value > 200 ? 'warning' : 'danger'} pill>
                                                                {value > 400 ? 'High' : value > 200 ? 'Medium' : 'Low'}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane tabId="trends">
                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0">
                                <CardHeader>
                                    <CardTitle className="mb-0 fw-semibold">Performance Trends</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {doughnutData?.labels?.map((label, index) => {
                                            const value = doughnutData.datasets[0].data[index];
                                            const trend = Math.random() * 20 - 10; // Mock trend data
                                            return (
                                                <Col md={6} key={label} className="mb-4">
                                                    <Card className="border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                                        <CardBody>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <h6 className="mb-0 fw-semibold">{label}</h6>
                                                                <small className={`fw-semibold ${trend > 0 ? 'text-success' : 'text-danger'}`}>
                                                                    {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                                                                </small>
                                                            </div>
                                                            <Progress value={75 + trend} color={trend > 0 ? 'success' : 'danger'} className="mb-2" />
                                                            <small className="text-muted">Current: {value} beneficiaries</small>
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>
        </div>
    );
};

export default GraphicalReport;